import React, { useEffect, useState } from "react";
import {
  StarIcon as StarIconOutline,
  PencilSquareIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import {
  useCreateReviewMutation,
  useDeleteReviewMutation,
  useUpdateReviewMutation,
} from "./reviewsApiSlice";
import { useNavigate } from "react-router-dom";
import { ReviewObj, UserObject } from "../../shared/types";

// MyReview accepts either an existing review or a seed for a brand-new one
type ReviewSeed = {
  _id?: string;
  id?: string;
  review?: string;
  rating?: number;
  createdAt?: string;
  user: Pick<UserObject, "_id"> & Partial<Pick<UserObject, "name" | "photo">>;
  tour?: string;
};

type Props = {
  reviewData: ReviewObj | ReviewSeed;
};

const STAR_ON = "#C08A2D";
const STAR_OFF = "#e3ddcf";

const MyReview = ({ reviewData }: Props) => {
  const navigate = useNavigate();
  const [eidtReview, setEditReview] = useState(
    "id" in reviewData ? false : true
  );
  const [stars, setStars] = useState(reviewData.rating ?? 0);
  const [review, setReview] = useState(reviewData.review ?? "");
  const [errMsg, setErrMsg] = useState("");

  const [
    createReview,
    {
      data: createReviewData,
      isSuccess: createIsSuccess,
      isLoading: createIsLoading,
      isError: createIsError,
      error: createError,
    },
  ] = useCreateReviewMutation();

  useEffect(() => {
    if (createIsError === true) {
      console.log("Error creating a new review: ", createError);
    } else if (createIsSuccess) {
      navigate(0);
    }
  }, [
    createReviewData,
    createIsSuccess,
    createIsLoading,
    createIsError,
    createError,
    navigate,
  ]);

  const [
    updateReview,
    {
      data: updateReviewData,
      isSuccess: updateIsSuccess,
      isLoading: updateIsLoading,
      isError: updateIsError,
      error: updateError,
    },
  ] = useUpdateReviewMutation();

  useEffect(() => {
    if (updateIsError === true) {
      console.log("Error updating the review data: ", updateError);
    } else if (updateIsSuccess) {
      setEditReview(false);
    }
  }, [
    updateReviewData,
    updateIsSuccess,
    updateIsLoading,
    updateIsError,
    updateError,
  ]);

  const [
    deleteReview,
    {
      data: deleteReviewData,
      isSuccess: deleteIsSuccess,
      isLoading: deleteIsLoading,
      isError: deleteIsError,
      error: deleteError,
    },
  ] = useDeleteReviewMutation();

  useEffect(() => {
    if (deleteIsError === true) {
      console.log("Error updating the review data: ", deleteError);
    } else if (deleteIsSuccess) {
      navigate(0);
    }
  }, [
    deleteReviewData,
    deleteIsSuccess,
    deleteIsLoading,
    deleteIsError,
    deleteError,
    navigate,
  ]);

  const onCreateReviewClick = () => {
    if (stars === 0 || review.length < 2) {
      setErrMsg(
        "Invalid inputs. Please enter both a star rating and a review message."
      );
    } else {
      createReview({
        review,
        rating: stars,
        tour: reviewData.tour ?? "",
        user: reviewData.user._id,
      });
    }
  };

  const onUpdateReviewClick = () => {
    if (review.length < 2) {
      setErrMsg("Invalid inputs. Please enter a review message.");
    } else {
      updateReview({
        reviewId: reviewData.id ?? "",
        rating: stars,
        review,
      });
    }
  };

  const onDeleteReviewClick = () => {
    deleteReview(reviewData.id ?? "");
  };

  const starClasses = `h-6 w-6 ${
    eidtReview ? "cursor-pointer" : "cursor-default"
  }`;

  const starsContent = [1, 2, 3, 4, 5].map((n) => (
    <StarIconOutline
      key={n}
      className={starClasses}
      onClick={() => (eidtReview ? setStars(n) : undefined)}
      fill={stars >= n ? STAR_ON : STAR_OFF}
    />
  ));

  const card =
    "w-full rounded-[18px] border border-line-200 bg-white p-6 shadow-[0_14px_30px_-22px_rgba(0,0,0,0.25)]";
  const textarea =
    "mt-3 w-full rounded-xl border border-line bg-field p-3 text-[15px] leading-relaxed text-ink outline-none placeholder:text-muted-300 focus:border-secondary-400";

  // Existing review (owned) — read/edit/delete
  return "id" in reviewData ? (
    <div className={card}>
      {errMsg && <p className="mb-3 text-[14px] text-hard">{errMsg}</p>}
      <div className="flex w-full items-center justify-between">
        <span className="flex">{starsContent}</span>
        <span className="flex gap-4">
          {eidtReview ? (
            <>
              <XMarkIcon
                className="h-6 w-6 cursor-pointer text-muted"
                onClick={() => setEditReview(false)}
              />
              <CheckIcon
                className="h-6 w-6 cursor-pointer text-secondary-400"
                onClick={onUpdateReviewClick}
              />
            </>
          ) : (
            <>
              <TrashIcon
                className="h-6 w-6 cursor-pointer text-hard"
                onClick={onDeleteReviewClick}
              />
              <PencilSquareIcon
                className="h-6 w-6 cursor-pointer text-muted"
                onClick={() => setEditReview(true)}
              />
            </>
          )}
        </span>
      </div>
      {eidtReview ? (
        <textarea
          placeholder={reviewData.review}
          value={review}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
            setReview(e.target.value)
          }
          className={textarea}
          rows={4}
        />
      ) : (
        <p className="mt-3 text-[15px] leading-relaxed text-[#3f4a43]">
          {reviewData.review}
        </p>
      )}
      <div className="mt-5 flex items-center gap-3">
        <img
          src={`${process.env.REACT_APP_API_URL}/img/users/${reviewData.user.photo}`}
          className="h-11 w-11 rounded-full object-cover"
          alt="reviewer"
          crossOrigin="anonymous"
        />
        <div>
          <div className="text-[14.5px] font-bold">{reviewData.user.name}</div>
          <div className="text-[12.5px] text-muted-300">
            {eidtReview
              ? ""
              : new Date(reviewData.createdAt ?? "").toLocaleDateString()}
          </div>
        </div>
      </div>
    </div>
  ) : (
    // New review — create form
    <div className={card}>
      {errMsg && <p className="mb-3 text-[14px] text-hard">{errMsg}</p>}
      <div className="flex w-full items-center justify-between">
        <span className="flex">{starsContent}</span>
        <button
          type="button"
          className="rounded-full bg-secondary-400 px-5 py-2 text-[14px] font-semibold text-white transition-colors hover:bg-ink"
          onClick={onCreateReviewClick}
        >
          Submit review
        </button>
      </div>
      <textarea
        placeholder="Leave a review here!"
        value={review}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
          setReview(e.target.value)
        }
        className={textarea}
        rows={5}
      />
    </div>
  );
};

export default MyReview;
